import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import SolarIcon from 'components/icons/SolarIcon';
import ClientSelected from '../../ClientSelected';
import { useCustomersList } from '../context/CustomersListProvider';

const CustomersFilter = () => {
    const { t } = useTranslation(['Clients', 'common']);
    const { filterClientId, setFilterClientId, filterVatEnabled, setFilterVatEnabled } =
        useCustomersList();
    const [filterClient, setFilterClient] = useState<ClientType | null>(null);
    const [show, setShow] = useState(false);
    const rootRef = useRef<HTMLSpanElement | null>(null);
    const filterActive = Boolean(filterClientId) || filterVatEnabled !== null;

    useEffect(() => {
        if (!filterClientId) {
            setFilterClient(null);
            return;
        }
        if (filterClient?.ID === filterClientId) return;

        let cancelled = false;
        (async () => {
            const resClient = await apiClient.getClientById(filterClientId);
            if (cancelled) return;
            setFilterClient(resClient || null);
            if (!resClient) setFilterClientId(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [filterClientId, filterClient?.ID, setFilterClientId]);

    useEffect(() => {
        if (!show) return;

        const onPointerDown = (event: globalThis.MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (rootRef.current?.contains(target)) return;
            if (target.closest('.object-filter-menu') || target.closest('.user-selected')) {
                return;
            }
            setShow(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [show]);

    const handleChangeClient = (client: ClientType | null) => {
        setFilterClient(client);
        setFilterClientId(client?.ID ? Number(client.ID) : null);
    };

    const handleResetFilters = () => {
        setFilterClient(null);
        setFilterClientId(null);
        setFilterVatEnabled(null);
    };

    const menu = (
        <Dropdown.Menu
            className="object-filter-menu p-3"
            renderOnMount
            popperConfig={{
                strategy: 'fixed',
                modifiers: [
                    {
                        name: 'preventOverflow',
                        options: { boundary: 'viewport', padding: 12 },
                    },
                    {
                        name: 'flip',
                        options: { fallbackPlacements: ['bottom-end', 'top-end', 'bottom-start'] },
                    },
                ],
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest('.user-selected')) {
                    e.stopPropagation();
                }
            }}
        >
            <div className="mb-3">
                <ClientSelected
                    title={t('subjects.filterClient')}
                    className="mb-0"
                    allowNew={false}
                    clearable
                    maxHeight={160}
                    value={filterClient}
                    onChange={handleChangeClient}
                />
            </div>

            <div className="mb-3">
                <div className="form-label text-muted mb-2">{t('subjects.filterVat')}</div>
                {(
                    [
                        { value: null, label: t('subjects.filterVatAll') },
                        { value: 1, label: t('subjects.filterVatYes') },
                        { value: 0, label: t('subjects.filterVatNo') },
                    ] as const
                ).map((option) => (
                    <label
                        key={String(option.value)}
                        className="d-flex align-items-center mb-1"
                    >
                        <input
                            type="radio"
                            className="form-check-input me-2"
                            name="customers-filter-vat"
                            checked={filterVatEnabled === option.value}
                            onChange={() => setFilterVatEnabled(option.value)}
                        />
                        {option.label}
                    </label>
                ))}
            </div>

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-0 min70px"
                    style={{ height: 24 }}
                    disabled={!filterActive}
                    onClick={handleResetFilters}
                >
                    {t('common:buttons.reset')}
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="mb-0 ms-2 min70px"
                    style={{ height: 24 }}
                    onClick={() => setShow(false)}
                >
                    {t('common:buttons.close')}
                </Button>
            </div>
        </Dropdown.Menu>
    );

    return (
        <span ref={rootRef} className="d-inline-block">
            <Dropdown
                align="end"
                autoClose={false}
                show={show}
                onToggle={(next, meta) => {
                    if (next) {
                        setShow(true);
                        return;
                    }
                    if (meta?.source === 'click') setShow(false);
                }}
            >
                <Dropdown.Toggle
                    className="fs-14 px-2 ms-1"
                    size="sm"
                    variant={filterActive ? 'danger' : 'primary'}
                    id="dropdown-customers-filter"
                    title={t('subjects.toolFilterClient')}
                >
                    <SolarIcon name="funnel" className="fs-14 p-0" />
                </Dropdown.Toggle>

                {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
            </Dropdown>
        </span>
    );
};

export default CustomersFilter;
